import axios, {AxiosResponse} from 'axios'
import {
  ExamQuestionAssignmentsResponse,
  ExamQuestionPoolDepartment,
  ExamQuestionPoolResponse,
  CreateManualExamPayload,
  AddExamQuestionsPayload,
  ReorderExamQuestionsPayload,
  UpdateOptionOrderPayload,
  UpdateManualExamBookletsPayload,
  GenerateManualExamBookletsPayload,
  ManualExamSettingsResponse,
  ManualExamAnswerKeyResponse,
  RemoveExamQuestionResponse,
  UpdateExamQuestionPointsPayload,
  UpdateManualExamStatusPayload,
  QuestionTagSummary,
  ManualExamCourseShareInfo,
} from './_models'

const API_URL = process.env.REACT_APP_API_URL
const CUSTOM_URL = `${API_URL}/Custom`

export const createManualExam = (payload: CreateManualExamPayload) => {
  return axios.post(`${CUSTOM_URL}/CreateManualExam`, payload)
}

export const uploadManualExamMaterial = (examId: number, file: File) => {
  const formData = new FormData()
  formData.append('examId', String(examId))
  formData.append('file', file)

  return axios.post(`${CUSTOM_URL}/UploadManualExamMaterial`, formData, {
    headers: {'Content-Type': 'multipart/form-data'},
  })
}

export const deleteManualExamMaterial = (examId: number) => {
  return axios.delete(`${CUSTOM_URL}/DeleteManualExamMaterial`, {params: {examId}})
}

export const getManualExamCourseShareInfo = async (
  semesterCourseId: number
): Promise<ManualExamCourseShareInfo | null> => {
  if (!Number.isFinite(semesterCourseId)) {
    return null
  }

  try {
    const response = await axios.get(`${CUSTOM_URL}/GetManualExamCourseShareInfo`, {
      params: {semesterCourseId},
    })
    const payload = response?.data
    if (payload && typeof payload === 'object') {
      const data = (payload as {data?: ManualExamCourseShareInfo | null}).data
      if (data && typeof data === 'object') {
        return data
      }
    }
    return null
  } catch (error) {
    return null
  }
}

export const getAutomaticExamName = async (
  semesterCourseId: number,
  examTypeId: number
): Promise<string> => {
  if (!Number.isFinite(semesterCourseId) || !Number.isFinite(examTypeId)) {
    return ''
  }

  try {
    const response = await axios.get(`${CUSTOM_URL}/GetAutomaticExamName`, {
      params: {semesterCourseId, examTypeId},
    })
    const data = response?.data
    if (typeof data === 'string') {
      return data
    }
    if (data && typeof data.name === 'string') {
      return data.name
    }
    if (data && typeof data.Name === 'string') {
      return data.Name
    }
  } catch (error) {
    return ''
  }

  return ''
}

export const getExamQuestionPoolDepartments = (semesterCourseId: number) => {
  return axios
    .get(`${CUSTOM_URL}/GetExamQuestionPoolDepartments`, {params: {semesterCourseId}})
    .then((res: AxiosResponse<ExamQuestionPoolDepartment[]>) => res.data)
}

export const getExamQuestionPool = (
  semesterCourseId: number,
  departmentId?: number,
  examId?: number,
  tagIds?: number[]
) => {
  const params: Record<string, string | number> = {semesterCourseId}
  if (typeof departmentId === 'number') {
    params.departmentId = departmentId
  }
  if (typeof examId === 'number') {
    params.examId = examId
  }
  if (Array.isArray(tagIds) && tagIds.length > 0) {
    const normalized = tagIds
      .map((id) => (typeof id === 'number' ? id : Number(id)))
      .filter((id) => Number.isFinite(id)) as number[]
    if (normalized.length > 0) {
      const unique = Array.from(new Set(normalized))
      params.tagIds = unique.join(',')
    }
  }

  return axios
    .get(`${CUSTOM_URL}/GetExamQuestionPool`, {
      params,
    })
    .then((res: AxiosResponse<ExamQuestionPoolResponse>) => res.data)
}

export const getExamAssignments = (examId: number) => {
  return axios
    .get(`${CUSTOM_URL}/GetExamQuestionAssignments`, {params: {examId}})
    .then((res: AxiosResponse<ExamQuestionAssignmentsResponse>) => res.data)
}

export const getManualExamSettings = (examId: number) => {
  return axios
    .get(`${CUSTOM_URL}/GetManualExamSettings`, {params: {examId}})
    .then((res: AxiosResponse<ManualExamSettingsResponse>) => res.data)
}

export const addExamQuestions = (payload: AddExamQuestionsPayload) => {
  return axios.post<ExamQuestionAssignmentsResponse>(
    `${CUSTOM_URL}/AddExamQuestions`,
    payload
  )
}

export const reorderExamQuestions = (payload: ReorderExamQuestionsPayload) => {
  return axios.post(`${CUSTOM_URL}/ReorderExamQuestions`, payload)
}

export const updateExamQuestionOptionOrder = (payload: UpdateOptionOrderPayload) => {
  return axios.post(`${CUSTOM_URL}/UpdateExamQuestionOptionOrder`, payload)
}

export const removeExamQuestion = (examQuestionId: number) => {
  return axios
    .delete(`${CUSTOM_URL}/RemoveExamQuestion/${examQuestionId}`)
    .then((res: AxiosResponse<RemoveExamQuestionResponse>) => res.data)
}

export const updateManualExamBooklets = (payload: UpdateManualExamBookletsPayload) => {
  return axios.post(`${CUSTOM_URL}/UpdateManualExamBooklets`, payload)
}

export const generateManualExamBooklets = (payload: GenerateManualExamBookletsPayload) => {
  return axios
    .post(`${CUSTOM_URL}/GenerateManualExamBooklets`, payload)
    .then((res: AxiosResponse<{data?: ManualExamSettingsResponse}>) => res.data?.data)
}

export const updateExamQuestionPoints = (payload: UpdateExamQuestionPointsPayload) => {
  return axios.post(`${CUSTOM_URL}/UpdateExamQuestionPoints`, payload)
}

export const getManualExamAnswerKeys = (examId: number) => {
  return axios
    .get(`${CUSTOM_URL}/GetManualExamAnswerKeys`, {params: {examId}})
    .then((res: AxiosResponse<{data?: ManualExamAnswerKeyResponse}>) => res.data?.data)
}

export const downloadManualExamPdf = (examId: number, spacingLevel?: number, format?: string) => {
  const params: Record<string, string | number> = {examId}
  if (typeof spacingLevel === 'number' && Number.isFinite(spacingLevel)) {
    const normalized = Math.min(Math.max(Math.round(spacingLevel), 1), 5)
    params.spacingLevel = normalized
  }

  if (typeof format === 'string' && format.trim().length > 0) {
    params.format = format.trim()
  }

  return axios.get(`${CUSTOM_URL}/DownloadManualExamBookletPdf`, {
    params,
    responseType: 'blob',
  })
}

export const updateManualExamStatus = (payload: UpdateManualExamStatusPayload) => {
  return axios.post(`${CUSTOM_URL}/UpdateManualExamStatus`, payload)
}

export const getMyQuestionTags = async (): Promise<QuestionTagSummary[]> => {
  try {
    const response = await axios.get(`${CUSTOM_URL}/GetMyQuestionTags`)
    const payload = response?.data
    if (Array.isArray(payload)) {
      return payload as QuestionTagSummary[]
    }
    if (payload && Array.isArray(payload.data)) {
      return payload.data as QuestionTagSummary[]
    }
    return []
  } catch (error) {
    return []
  }
}

